import React, { FC } from 'react';
import { Flex, Checkbox, Text, VStack, StackProps, Box, VisuallyHidden } from '@chakra-ui/react';
import _ from 'lodash';
import { LineItem } from '@olo-web/components/atoms/LineItem';
import { useOrder } from '@olo-web/domain/orders/queries/useOrder';
import { formatPrice } from '@olo-web/domain/orders/functions/formatPrice';
import { useCheckedGuests } from '@olo-web/domain/orders/hooks/useCheckedGuests';
import { useMount } from 'react-use';
import { useCheckoutDispatch, useSavedDineInContextState } from '@olo-web/client-state';

type GuestNamePropsType = {
  name: string;
  onlyName?: boolean;
  isFaded: boolean;
  guestId?: string;
  ariaDescription?: string;
};

const GuestName: FC<GuestNamePropsType> = ({
  name,
  onlyName = false,
  isFaded,
  ariaDescription,
  guestId,
}) => {
  return (
    <Box
      as="td"
      tabIndex={0}
      fontWeight={onlyName ? undefined : 400}
      fontSize="sm"
      opacity={isFaded ? 0.55 : 1}
    >
      {ariaDescription && <VisuallyHidden id={guestId}>{ariaDescription}</VisuallyHidden>}
      <Text
        display="inline"
        whiteSpace="nowrap"
        overflow="hidden"
        textOverflow="ellipsis"
        maxW="10ch"
        aria-details={guestId}
        aria-hidden={!!ariaDescription}
      >
        {`${name}${onlyName ? '' : "'s total"}`}
      </Text>
    </Box>
  );
};

export const GuestTotalsList = ({
  guests,
  showCheckBox = false,
  showPaid = false,
  fadeIfPaid = false,
  containerProps = {},
  itemProps = {},
  showTotal = false,
  showNegativeGuestAmount = false,
}: {
  guests: IGuest[];
  showCheckBox?: boolean;
  showPaid?: boolean;
  fadeIfPaid?: boolean;
  containerProps?: StackProps;
  showTotal?: boolean;
  showNegativeGuestAmount?: boolean;
  itemProps?: StackProps;
}) => {
  const { updateGuestTotals, guests: checkedGuests } = useCheckedGuests();
  const { data: order } = useOrder();
  const checkoutDispatch = useCheckoutDispatch();
  const savedDineInState = useSavedDineInContextState();
  const initiatorGuest = guests?.find((guest) => guest?.id === savedDineInState?.guest?.id);

  useMount(() => {
    if (
      initiatorGuest !== undefined &&
      checkedGuests?.length === 0 &&
      Number(initiatorGuest?.groupOrderBalanceAmount) !== 0
    ) {
      checkoutDispatch({
        type: 'SET_CHECKED_GUESTS',
        payload: [initiatorGuest],
      });
    }
  });

  return (
    <VStack direction="column" width="100%" spacing={0} {...containerProps}>
      {guests?.map((guest: IGuest, index) => {
        const showAsPaid = guest.groupOrderBalancePaidFlag && showPaid;
        const isFaded = showAsPaid && fadeIfPaid;
        const showChipPaid = showAsPaid || order?.balanceDueAmount === '0';
        const guestAmount = `${showNegativeGuestAmount ? '-' : ''}${formatPrice(
          guest.groupOrderBalanceAmount
        )}`;

        if (Number(guest?.groupOrderBalanceAmount) !== 0) {
          return (
            <Flex
              height={6}
              mb={showCheckBox ? '12px !important' : 0}
              flex="1 1 auto"
              fontWeight="600"
              key={guest.id}
              direction="row"
              justifyContent="space-between"
              alignItems="center"
              padding={1}
              paddingRight={!showCheckBox && 0}
              paddingLeft={!showCheckBox && 0}
              width="100%"
              {...itemProps}
              data-testid={`name-and-total-guest-${index + 1}`}
            >
              <Flex alignItems="center" flexGrow={1} flexShrink={1}>
                {showCheckBox ? (
                  <Checkbox
                    colorScheme="primary"
                    opacity={showAsPaid ? 0.55 : 1}
                    fontSize="sm"
                    isChecked={checkedGuests.some((cg) => cg.id === guest.id) || showAsPaid}
                    sx={{
                      '& .chakra-checkbox__control': {
                        borderColor: 'black',
                      },
                      '& [data-checked]': {
                        borderColor: 'primary.300',
                      },
                      '& [data-active]': {
                        borderColor: 'primary.300',
                      },
                    }}
                    isDisabled={showAsPaid}
                    onChange={(e) => {
                      if (showAsPaid) {
                        e.preventDefault();
                        e.stopPropagation();
                        return;
                      }
                      updateGuestTotals(guest);
                    }}
                    data-testid={`checkbox-input-guest-${index + 1}`}
                  >
                    <GuestName
                      ariaDescription={`${guest.name}${showChipPaid ? ` paid ${guestAmount}` : ''}`}
                      isFaded={isFaded}
                      name={guest.name}
                      guestId={guest.id}
                      onlyName
                    />
                  </Checkbox>
                ) : (
                  <GuestName isFaded={isFaded} name={guest.name} />
                )}
                {showChipPaid && (
                  <Flex
                    width="43px"
                    height="22px"
                    borderRadius={4}
                    backgroundColor="#E0F5EA"
                    alignItems="center"
                    justifyContent="center"
                    ml={showCheckBox ? 4 : 2}
                    aria-hidden={true}
                  >
                    <Text fontWeight="400" fontSize="xs" data-testid="paid-chip">
                      Paid
                    </Text>
                  </Flex>
                )}
              </Flex>
              <Text
                tabIndex={showAsPaid || !showCheckBox ? -1 : 0}
                display="inline-flex"
                alignSelf="center"
                fontSize="sm"
                fontWeight="400"
                opacity={isFaded ? 0.55 : 1}
                aria-hidden={showAsPaid && showCheckBox}
              >
                {guestAmount}
              </Text>
            </Flex>
          );
        }
      })}
      {showTotal && (
        <LineItem
          isBolded
          label="Total"
          amount={guests
            ?.reduce((sum, guest) => (sum += +guest.groupOrderBalanceAmount), 0)
            .toFixed(2)}
        />
      )}
    </VStack>
  );
};
